import React from "react";
import { connect } from "react-redux";
import { Col, Badge } from "react-bootstrap";
import PropTypes from "prop-types";

import Table from "../../../layout/table";
import ConditionalDropdown from "../../../layout/conditional-dropdown";
import ConditionalButton from "../../../layout/conditional-button";
import ScrollToTopButton from "./scroll-to-top-button";
import ImportModal from "../surveys/import";

import { send as pageInfo, loading } from "../../../actions/page";
import { get as getTemplates, global as getGlobalEmailTemplates, register as registerEmailTemplate } from "../../../actions/email.template";
import { get as getReports, global as getGlobalReports, register as registerReport } from "../../../actions/report.template";
import { get as getSetting } from "../../../actions/settings";
import { withNavigation } from "../../../utils/wrapper";

const EMAIL_TEMPLATE = "email";
const REPORT_TEMPLATE = "report";
const LOAD_TIMEOUT_MS = 10000;

const asArray = (value) => Array.isArray(value) ? value : [];

const withTimeout = (promise, fallback, timeout = LOAD_TIMEOUT_MS) =>
  new Promise((resolve) => {
    let settled = false;
    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      resolve(fallback);
    }, timeout);

    Promise.resolve(promise)
      .then((value) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        resolve(value ?? fallback);
      })
      .catch(() => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        resolve(fallback);
      });
  });

class TemplateList extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      size: 10,
      configs: [],
      system: null,
      globals: [],
      modal: {
        import: {
          show: false,
        },
      },
    };

    this.mounted = false;
  }

  get headers() {
    return [
      {
        header: "NAME",
        sortable: false,
        source: "name",
        className: "text-muted text-small text-uppercase w-25",
        onRender: this.onNameLinkFormat,
      },
      {
        header: "SUBJECT",
        sortable: false,
        source: "subject",
        className: "text-muted text-small text-uppercase w-45",
        onRender: this.onLinkFormat,
      },
      {
        header: "CATEGORY",
        sortable: true,
        source: "category",
        className: "text-muted text-small text-uppercase w-15",
        onRender: this.onLinkFormat,
      },
      {
        header: "TYPE",
        sortable: true,
        source: "style",
        className: "text-muted text-small text-uppercase w-15",
        onRender: this.onLinkFormat,
      },
    ];
  }

  toTemplateHref = (row) => `/dashboard/templates/${row.style}/${row.uuid}`;

  onLinkFormat = (value, row) => {
    return <a href={this.toTemplateHref(row)}>{value}</a>;
  };

  onNameLinkFormat = (value, row) => {
    const isDefault = row?.name === this.state.system;
    return (
      <a href={this.toTemplateHref(row)}>
        {value}{" "}
        {isDefault ? (
          <Badge pill bg="dark" className="ms-2">
            Default
          </Badge>
        ) : null}
      </a>
    );
  };

  loadData = async () => {
    this.props.loading(true);
    try {
      const [configsRaw, reportsRaw, system] = await Promise.all([
        withTimeout(this.props.getTemplates(), []),
        withTimeout(this.props.getReports(), []),
        withTimeout(this.props.getSetting({ name: "FERA_EMAIL_CONFIGURATION" }), null),
      ]);

      const configs = asArray(configsRaw).map((o) => ({
        ...o,
        style: EMAIL_TEMPLATE,
      }));

      const reports = asArray(reportsRaw).map((o) => ({
        ...o,
        subject: o.caption,
        style: REPORT_TEMPLATE,
      }));

      if (this.mounted) this.setState({
        configs: [...configs, ...reports],
        system: system?.value ?? null,
      });
    } finally {
      this.props.loading(false);
    }

    this.loadGlobalTemplates();
  };

  loadGlobalTemplates = async () => {
    const [emailsRaw, reportsRaw] = await Promise.all([
      withTimeout(this.props.getGlobalEmailTemplates(), []),
      withTimeout(this.props.getGlobalReports(), []),
    ]);

    const emails = asArray(emailsRaw).map((o) => ({
      ...o,
      style: EMAIL_TEMPLATE,
      caption: o.caption || o.name,
      description: ["Email template", o.subject || o.category].filter(Boolean).join(" - "),
    }));

    const reports = asArray(reportsRaw).map((o) => ({
      ...o,
      style: REPORT_TEMPLATE,
      description: ["Report template", o.description || o.caption || o.fileName || o.output].filter(Boolean).join(" - "),
    }));

    const globals = [...emails, ...reports].sort((a, b) =>
      String(a.name || "").localeCompare(String(b.name || ""))
    );

    if (this.mounted) this.setState({ globals });
  };

  componentDidMount() {
    this.mounted = true;
    this.props.pageInfo({ title: "Templates", breadcrumb: "Templates" });
    this.loadData();
  }

  componentWillUnmount() {
    this.mounted = false;
    this.props.loading(false);
  }

  onNewEmailClick = () => {
    this.props.navigate("email/add");
  };

  onNewReportClick = () => {
    this.props.navigate("report/add");
  };

  onImportClick = () => {
    this.setState((prev) => ({
      modal: {
        ...prev.modal,
        import: {
          show: true,
        },
      },
    }));
  };

  onImportCancel = () => {
    this.setState((prev) => ({
      modal: {
        ...prev.modal,
        import: {
          show: false,
        },
      },
    }));
  };

  onImportOk = async (e, item) => {
    const isEmail = item.style === EMAIL_TEMPLATE;
    const registerTemplate = isEmail
      ? this.props.registerEmailTemplate
      : this.props.registerReport;

    const result = await registerTemplate({
      parentUuid: item.uuid,
      name: item.name,
      caption: item.caption || item.name,
      flags: 0,
      hidden: false,
      configuration: item.meta || {},
    });

    await this.loadData();
    this.onImportCancel();

    if (result?.uuid) {
      this.props.navigate(`${isEmail ? EMAIL_TEMPLATE : REPORT_TEMPLATE}/${result.uuid}`);
    }
  };

  render() {
    return (
      <>
        <Col md="8" className="float-right text-end">
          <ConditionalButton
            className="ms-sm-1"
            enabledVariant="outline-primary"
            display
            enabled
            onEnabledClick={this.onImportClick}
            icon="download"
          >
            Import
          </ConditionalButton>
          <ConditionalDropdown
            className="ms-sm-1"
            enabledVariant="secondary"
            enabled
            display
            items={[
              {
                onClick: this.onNewEmailClick,
                description: "create a new email template",
                caption: "Email",
              },
              {
                onClick: this.onNewReportClick,
                description: "create a new report template",
                caption: "Report",
              },
            ]}
          >
            New Template
          </ConditionalDropdown>
        </Col>

        <Col xs="12">
            <Table
                headers={this.headers}
                page={this.state.configs}
                size={this.state.size}
                index={0}
                pages={1}
                searchable={false}
                sizable={false}
                sortable={false}
            />
        </Col>
        <ImportModal
          show={this.state.modal.import.show}
          title="Import Template"
          data={this.state.globals}
          onOk={this.onImportOk}
          onCancel={this.onImportCancel}
        />
        <ScrollToTopButton />
      </>
    );
  }
}

TemplateList.propTypes = {
  auth: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  auth: state.auth,
});

export default withNavigation(
  connect(mapStateToProps, {
    pageInfo,
    loading,
    getTemplates,
    getReports,
    getGlobalEmailTemplates,
    getGlobalReports,
    registerEmailTemplate,
    registerReport,
    getSetting
  })(TemplateList)
);
